import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import moment from "moment";
import secureLocalStorage from "react-secure-storage";
import { getValidOrderCount } from "../modules/utilities/utility";

dayjs.extend(duration);

export const isIterableArray = (array) =>
  Array.isArray(array) && !!array.length;

//===============================
// Breakpoints
//===============================
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1540,
};

export const getItemFromStore = (key, defaultValue, store = localStorage) => {
  try {
    return store.getItem(key) === null
      ? defaultValue
      : JSON.parse(store.getItem(key));
  } catch {
    return store.getItem(key) || defaultValue;
  }
};

export const setItemToStore = (key, payload, store = localStorage) =>
  store.setItem(key, payload);

export const getStoreSpace = (store = localStorage) =>
  parseFloat(
    (
      escape(encodeURIComponent(JSON.stringify(store))).length /
      (1024 * 1024)
    ).toFixed(2)
  );

//===============================
// Cookie
//===============================
export const getCookieValue = (name) => {
  const value = document.cookie.match(
    "(^|[^;]+)\\s*" + name + "\\s*=\\s*([^;]+)"
  );
  return value ? value.pop() : null;
};

export const createCookie = (name, value, cookieExpireTime) => {
  const date = new Date();
  date.setTime(date.getTime() + cookieExpireTime);
  const expires = "; expires=" + date.toUTCString();
  document.cookie = name + "=" + value + expires + "; path=/";
};

export const numberFormatter = (number, fixed = 2) => {
  // Nine Zeroes for Billions
  return Math.abs(Number(number)) >= 1.0e9
    ? (Math.abs(Number(number)) / 1.0e9).toFixed(fixed) + "B"
    : // Six Zeroes for Millions
    Math.abs(Number(number)) >= 1.0e6
    ? (Math.abs(Number(number)) / 1.0e6).toFixed(fixed) + "M"
    : // Three Zeroes for Thousands
    Math.abs(Number(number)) >= 1.0e3
    ? (Math.abs(Number(number)) / 1.0e3).toFixed(fixed) + "K"
    : Math.abs(Number(number)).toFixed(fixed);
};

//===============================
// Colors
//===============================
export const hexToRgb = (hexValue) => {
  let hex;
  hexValue.indexOf("#") === 0
    ? (hex = hexValue.substring(1))
    : (hex = hexValue);
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b)
  );
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : null;
};

export const rgbColor = (color = colors[0]) => `rgb(${hexToRgb(color)})`;
export const rgbaColor = (color = colors[0], alpha = 0.5) =>
  `rgba(${hexToRgb(color)},${alpha})`;

export const colors = [
  "#2c7be5",
  "#00d97e",
  "#e63757",
  "#39afd1",
  "#fd7e14",
  "#02a8b5",
  "#727cf5",
  "#6b5eae",
  "#ff679b",
  "#f6c343",
];

export const themeColors = {
  primary: "#2c7be5",
  secondary: "#748194",
  success: "#00d27a",
  info: "#27bcfd",
  warning: "#f5803e",
  danger: "#e63757",
  light: "#f9fafd",
  dark: "#0b1727",
};

export const grays = {
  white: "#fff",
  100: "#f9fafd",
  200: "#ffffff",
  300: "#d8e2ef",
  400: "#b6c1d2",
  500: "#9da9bb",
  600: "#748194",
  700: "#5e6e82",
  800: "#4d5969",
  900: "#344050",
  1000: "#232e3c",
  1100: "#0b1727",
  black: "#000",
};

export const darkGrays = {
  white: "#fff",
  1100: "#f9fafd",
  1000: "#ffffff",
  900: "#d8e2ef",
  800: "#b6c1d2",
  700: "#9da9bb",
  600: "#748194",
  500: "#5e6e82",
  400: "#4d5969",
  300: "#344050",
  200: "#232e3c",
  100: "#0b1727",
  black: "#000",
};

export const getGrays = (isDark) => (isDark ? darkGrays : grays);

export const rgbColors = colors.map((color) => rgbColor(color));
export const rgbaColors = colors.map((color) => rgbaColor(color));

export const getColor = (name, dom = document.documentElement) => {
  return getComputedStyle(dom).getPropertyValue(`--falcon-${name}`).trim();
};

//===============================

// Echarts
//===============================
export const getPosition = (pos, params, dom, rect, size) => ({
  top: pos[1] - size.contentSize[1] - 10,
  left: pos[0] - size.contentSize[0] / 2,
});
//===============================
// E-Commerce
//===============================
export const calculateSale = (base, less = 0, fix = 2) =>
  (base - base * (less / 100)).toFixed(fix);
export const getTotalPrice = (cart, baseItems) =>
  cart.reduce((accumulator, currentValue) => {
    const { id, quantity } = currentValue;
    const { price, sale } = baseItems.find((item) => item.id === id);
    return accumulator + calculateSale(price, sale) * quantity;
  }, 0);
export const getSubtotal = (items) =>
  items.reduce((acc, curr) => curr.price * curr.quantity + acc, 0);
export const getDiscountPrice = (total, discount) =>
  total - total * (discount / 100);

export const getProductsQuantity = (products) =>
  products.reduce((acc, product) => product.quantity + acc, 0);

//===============================
// Helpers
//===============================
export const getPaginationArray = (totalSize, sizePerPage) => {
  const noOfPages = Math.ceil(totalSize / sizePerPage);
  const array = [];
  let pageNo = 1;
  while (pageNo <= noOfPages) {
    array.push(pageNo);
    pageNo = pageNo + 1;
  }
  return array;
};

export const capitalize = (str) =>
  (str.charAt(0).toUpperCase() + str.slice(1)).replace(/-/g, " ");

export const camelize = (str) => {
  return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
    if (+match === 0) return ""; // or if (/\s+/.test(match)) for white spaces
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  });
};

export const dashed = (str) => {
  return str.toLowerCase().replaceAll(" ", "-");
};

//routes helper

export const flatRoutes = (childrens) => {
  const allChilds = [];

  const flatChild = (childrens) => {
    childrens.forEach((child) => {
      if (child.children) {
        flatChild(child.children);
      } else {
        allChilds.push(child);
      }
    });
  };
  flatChild(childrens);

  return allChilds;
};

export const getFlatRoutes = (children) =>
  children.reduce(
    (acc, val) => {
      if (val.children) {
        return {
          ...acc,
          [camelize(val.name)]: flatRoutes(val.children),
        };
      } else {
        return {
          ...acc,
          unTitled: [...acc.unTitled, val],
        };
      }
    },
    { unTitled: [] }
  );

export const routesSlicer = ({ routes, columns = 3, rows }) => {
  const routesCollection = [];
  routes.map((route) => {
    if (route.children) {
      return route.children.map((item) => {
        if (item.children) {
          return routesCollection.push(...item.children);
        }
        return routesCollection.push(item);
      });
    }
    return routesCollection.push(route);
  });

  const totalRoutes = routesCollection.length;
  const calculatedRows = rows || Math.ceil(totalRoutes / columns);
  const routesChunks = [];
  for (let i = 0; i < totalRoutes; i += calculatedRows) {
    routesChunks.push(routesCollection.slice(i, i + calculatedRows));
  }
  return routesChunks;
};

export const getPageName = (pageName) => {
  return window.location.pathname.split("/").slice(-1)[0] === pageName;
};

export const copyToClipBoard = (textFieldRef) => {
  const textField = textFieldRef.current;
  textField.focus();
  textField.select();
  document.execCommand("copy");
};

export const reactBootstrapDocsUrl = "https://react-bootstrap.github.io";

export const pagination = (currentPage, size) => {
  const pages = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  let prev = currentPage - 1 - Math.floor(size / 2);

  if (currentPage - 1 - Math.floor(size / 2) < 0) {
    prev = 0;
  }
  if (currentPage - 1 - Math.floor(size / 2) > pages.length - size) {
    prev = pages.length - size;
  }
  const next = prev + size;

  return pages.slice(prev, next);
};

export const tooltipFormatter = (params) => {
  let tooltipItem = ``;
  params.forEach((el) => {
    tooltipItem =
      tooltipItem +
      `<div class='ms-1'> 
        <h6 class="text-700"><span class="fas fa-circle me-1 fs--2" style="color:${
          el.borderColor ? el.borderColor : el.color
        }"></span>
          ${el.seriesName} : ${
        typeof el.value === "object" ? el.value[1] : el.value
      }
        </h6>
      </div>`;
  });
  return `<div>
            <p class='mb-2 text-600'>
              ${
                dayjs(params[0].axisValue).isValid()
                  ? dayjs(params[0].axisValue).format("MMM DD")
                  : params[0].axisValue
              }
            </p>
            ${tooltipItem}
          </div>`;
};

export const addIdField = (items) => {
  return items.map((item, index) => ({
    id: index + 1,
    ...item,
  }));
};

// get file size

export const getSize = (size) => {
  if (size < 1024) {
    return `${size} Byte`;
  } else if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  } else {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }
};

/* Get A Random Number */
export const getRandomNumber = (min, max) => {
  return Math.floor(Math.random() * (max - min) + min);
};

/* get Dates between */

export const getDates = (
  startDate,
  endDate,
  interval = 1000 * 60 * 60 * 24
) => {
  const duration = endDate - startDate;
  const steps = duration / interval;
  return Array.from(
    { length: steps + 1 },
    (v, i) => new Date(startDate.valueOf() + interval * i)
  );
};

/* Get Past Dates */
export const getPastDates = (duration) => {
  let days;

  switch (duration) {
    case "week":
      days = 7;
      break;
    case "month":
      days = 30;
      break;
    case "year":
      days = 365;
      break;

    default:
      days = duration;
  }

  const date = new Date();
  const endDate = date;
  const startDate = new Date(new Date().setDate(date.getDate() - (days - 1)));
  return getDates(startDate, endDate);
};

// Add id to items in array
export const addId = (items) =>
  items.map((item, index) => ({
    id: index + 1,
    ...item,
  }));

//
export const getTimeDuration = (startDate, endDate, format = "") => {
  return dayjs.duration(endDate.diff(startDate)).format(format);
};

// Get Percentage
export const getPercentage = (number, percent) => {
  return (Number(number) / 100) * Number(percent);
};

//get chunk from array
export const chunk = (arr, chunkSize = 1, cache = []) => {
  const tmp = [...arr];
  if (chunkSize <= 0) return cache;
  while (tmp.length) cache.push(tmp.splice(0, chunkSize));
  return cache;
};

// Slugify text
export const slugifyText = (str) =>
  str
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");

export function minutesToHoursMinutesString(totalMinutes) {
  let hours = 0;
  let minutes = 0;

  if (!isNaN(totalMinutes)) {
    hours = Math.floor(totalMinutes / 60);
    minutes = totalMinutes % 60;
  }

  return `${hours}h ${minutes}m`;
}

export function secondsToMinutesSecondsString(totalSeconds) {
  let minutes = 0;
  let seconds = 0;

  if (!isNaN(totalSeconds)) {
    minutes = Math.floor(totalSeconds / 60);
    seconds = totalSeconds % 60;
  }

  return `${minutes}m ${seconds}s`;
}

export function capitalizeStringAccordingToNAValues(input) {
  if (!input) {
    return input;
  }
  if (input.length < 4) {
    return input.toUpperCase();
  }
  return input
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function convertTo12HourFormat(time) {
  return moment(time, "HH:mm").format("h:mm A");
}

export const TypeOfComment = {
  ENGAGEMENT: "engagement_comment",
  AI_GENERATED: "ai_generated_comment",
  HAIR_COACH: "hair_coach_comment",
  SQADSTACK_BOT: "sqadstack_bot_comment",
};

export function getTagText(type) {
  switch (type) {
    case TypeOfComment.ENGAGEMENT:
      return "Engagement Comment";
    case TypeOfComment.AI_GENERATED:
      return "AI Generated Comment";
    case TypeOfComment.HAIR_COACH:
      return "Hair Coach Comment";
    default:
      return "Hair Coach Comment";
  }
}

//===============================
// Address Formatting
//===============================
export const formatCustomerAddress = (details) => {
  if (!details) return '';

  const parts = [
    details.address1,
    details.address2,
    details.city,
    details.province,
    details.zip
  ].filter(part => part && part.trim() !== '');

  return parts.join(', ');
};

//===============================
// OTP Method Display
//===============================
export const getMethodDisplayName = (method) => {
  switch(method) {
    case 'SMS': return 'SMS';
    case 'WABA': return 'WhatsApp';
    case 'EMAIL': return 'Email';
    default: return method;
  }
};

export const ALLOWED_EMAILS_FOR_OTP_BYPASS = [
  'listonthomas@traya.health',
  'nirajsurve@traya.health',
  'agarwalsandip@traya.health',
  'nirbhay@traya.health',
  'sultantippu@traya.health'
];

/**
 * Gradual feature rollout using a deterministic UUID prefix bucket.
 *
 * How it works:
 * - Takes the first two hexadecimal characters of the UUID
 * - Converts them into a number between 0–255
 * - Allows users whose bucket value falls under the rollout threshold
 *
 * Assumption:
 * - UUIDs are sufficiently random in their hex distribution (UUID v4),
 *   making prefix-based bucketing evenly distributed.
 *
 * Rollout ladder (bucket ranges):
 * 12.5% -> 00–1f (0–31)
 * 25%   -> 00–3f (0–63)
 * 50%   -> 00–7f (0–127)
 * 75%   -> 00–bf (0–191)
 * 100%  -> 00–ff (0–255)
 *
 * ⚠️ Important:
 * - Do NOT change the bucketing logic once live
 * - Only increase the rollout percentage
 */
export const isInRollout = (uuid, rolloutPercent) => {
  if (typeof uuid !== 'string') return false;

  const prefix = uuid.slice(0, 2).toLowerCase();

  // Validate hexadecimal prefix
  if (!/^[0-9a-f]{2}$/.test(prefix)) return false;

  const value = parseInt(prefix, 16); // 0–255

  return value < (256 * rolloutPercent) / 100;
};

//===============================
// OTP Modal Decision Logic
//===============================
/**
 * Determines whether the OTP modal should be shown during order placement.
 *
 * Conditions:
 * 1. User email is NOT in the whitelist (ALLOWED_EMAILS_FOR_OTP_BYPASS)
 * 2. Backend feature flag (PLACE_ORDER_VIA_OTP) must be enabled.
 * 3. User must be a LEAD (0 valid orders), not a CUSTOMER (≥1 valid order)
 * 4. The user must fall within the configured rollout percentage,
 *    determined deterministically using the caseId (UUID).
 *
 * Business Logic:
 * - CUSTOMER: Has ≥1 non-void order → Don't show OTP (trusted user)
 * - LEAD: Has 0 valid orders → Show OTP (if other conditions pass)
 *
 * ⚠️ Important:
 * - Whitelisted users always bypass OTP regardless of other settings
 * - Do NOT change the rollout logic once live
 * - Only adjust the rollout percentage (12.5 → 25 → 50 → 75 → 100)
 *
 * @param {boolean} otpEnabled - Backend setting PLACE_ORDER_VIA_OTP value
 * @param {string} caseId - Customer case ID (UUID format)
 * @param {Array} orders - Array of customer orders (optional, defaults to [])
 * @param {string} gender - Customer gender ('M' for male, 'F' for female)
 * @returns {boolean} - true if OTP modal should be shown
 */
export const shouldShowOTPModal = (otpEnabled, caseId, orders = [], gender) => {
  // First check: If backend flag is disabled, OTP must never be shown
  if (!otpEnabled) return false;

  // Second check: Whitelisted users always bypass OTP
  const userEmail = secureLocalStorage.getItem('email');
  if (ALLOWED_EMAILS_FOR_OTP_BYPASS.includes(userEmail ?? '')) {
    return false;
  }

  // Third check: Customer vs Lead distinction
  // Get count of valid (non-void, non-unknown) orders
  const validOrderCount = getValidOrderCount(orders);

  // If user has at least 1 valid order, they're a CUSTOMER → Don't show OTP
  if (validOrderCount > 0) {
    return false;
  }

  // User is a LEAD (0 valid orders) → Check rollout percentage
  // Fourth check: Deterministic rollout based on UUID
  // Exclude case IDs starting with 2 or 3 (non-OTP consent flow)
  if (isNonOTPConsentCase(caseId)) return false;

  return isInRollout(caseId, 100); // 100% rollout for leads
};

//===============================
// Non-OTP Consent Modal Logic
//===============================
/**
 * Checks if a case ID falls in the non-OTP consent bucket.
 * Case IDs whose first hex character is '2' or '3' (decimal 32–63).
 *
 * @param {string} caseId - Customer case ID (UUID format)
 * @returns {boolean}
 */
export const isNonOTPConsentCase = (caseId) => {
  if (typeof caseId !== 'string') return false;
  const firstChar = caseId[0]?.toLowerCase();
  return firstChar === '2' || firstChar === '3';
};

/**
 * Determines whether the non-OTP consent modal should be shown.
 *
 * Same lead/whitelist checks as OTP, but only for case IDs starting with 2 or 3.
 *
 * @param {boolean} otpEnabled - Backend setting PLACE_ORDER_VIA_OTP value
 * @param {string} caseId - Customer case ID (UUID format)
 * @param {Array} orders - Array of customer orders (optional)
 * @returns {boolean}
 */
export const shouldShowNonOTPModal = (otpEnabled, caseId, orders = []) => {
  if (!otpEnabled) return false;

  const userEmail = secureLocalStorage.getItem('email');
  if (ALLOWED_EMAILS_FOR_OTP_BYPASS.includes(userEmail ?? '')) {
    return false;
  }

  const validOrderCount = getValidOrderCount(orders);
  if (validOrderCount > 0) return false;

  return isNonOTPConsentCase(caseId);
};

//===============================
// Web Native Link Rollout
//===============================
/**
 * Determines whether the web native link (copy/reorder) should be disabled.
 *
 * Only applies to LEADS (0 valid orders). Customers (≥1 valid order) always
 * keep the link enabled.
 *
 * For leads, case IDs whose first hex character is 0 or 1 (prefix 00–1f,
 * 12.5% bucket) have the link disabled.
 *
 * @param {string} caseId - Customer case ID (UUID format)
 * @param {Array} orders - Array of customer orders (optional, defaults to [])
 * @returns {boolean} - true if the web native link should be disabled
 */
export const shouldDisableWebNativeLink = (caseId, orders = []) => {
  const validOrderCount = getValidOrderCount(orders);
  // Customers (≥1 valid order) always keep the link
  if (validOrderCount > 0) return false;
  // Leads only: check rollout bucket
  return isInRollout(caseId, 12.5);
};

/**
 * Determines whether the Buy Now button should be hidden on the result page.
 *
 * Only hides for LEADS (0 valid orders) whose case ID falls in the 12.5% bucket
 * (first hex char is 0 or 1). Customers (≥1 valid order) always see Buy Now.
 *
 * @param {string} caseId - Customer case ID (UUID format)
 * @param {Array} orders - Array of customer orders (optional, defaults to [])
 * @returns {boolean} - true if Buy Now should be hidden
 */
export const shouldHideResultPageBuyNow = (caseId, orders = []) => {
  const validOrderCount = getValidOrderCount(orders);
  if (validOrderCount > 0) return false;
  return isInRollout(caseId, 12.5);
};

//===============================
// Sticky Doctor Rollout
//===============================
/**
 * Determines whether sticky doctor feature is enabled for a case.
 *
 * @param {string} caseId - Customer case ID (UUID format)
 * @param {number|string} interventionDoctor - The intervention_doctor ID from case data
 * @returns {boolean} - true if sticky doctor should be enabled
 */
export const isStickyDoctorRollout = (caseId, interventionDoctor, rolloutPercentage = 0) => {
  if (!interventionDoctor) return false;
  return isInRollout(caseId, rolloutPercentage);
};

//===============================
// Text Highlighting
//===============================
/**
 * Highlights matched search term in text with styled spans
 * @param {string} text - Text to search within
 * @param {string} searchTerm - Term to highlight (case-insensitive)
 * @returns {string|Array} - Text with highlighted matches or original text
 */
export const highlightText = (text, searchTerm) => {
  if (!text || !searchTerm || !searchTerm.trim()) {
    return text;
  }

  const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedTerm})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) => {
    if (regex.test(part)) {
      return (
        <span
          key={index}
          style={{
            backgroundColor: '#fff566',
            fontWeight: 600,
            padding: '0 2px',
            borderRadius: '2px'
          }}
        >
          {part}
        </span>
      );
    }
    return part;
  });
};
